import React, { useState, useRef, useEffect, useId } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { extractRegionCode } from '../../config/regionCodes';

interface AddressData {
  address: string;
  label: string;
  postcode: string;
  postalCode: string;
  city: string;
  regionCode?: string | null;
}

interface AddressSuggestion {
  label: string;
  properties: {
    name: string;
    postcode: string;
    city: string;
    context: string;
  };
}

interface AddressAutocompleteProps {
  value: string;
  onChange?: (value: string) => void;
  onAddressSelect: (data: AddressData) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  required?: boolean;
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  onBlur,
  placeholder = "Tapez votre adresse...",
  className = '',
  disabled = false,
  label,
  error,
  required
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const searchAddressesFR = async (searchQuery: string): Promise<AddressSuggestion[]> => {
    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(searchQuery)}&limit=5`
    );
    if (!response.ok) throw new Error('Erreur lors de la recherche d\'adresse');
    const data = await response.json();
    return data.features.map((feature: any) => ({
      label: feature.properties.label,
      properties: {
        name: feature.properties.name,
        postcode: feature.properties.postcode,
        city: feature.properties.city,
        context: feature.properties.context
      }
    }));
  };

  const searchAddresses = async (searchQuery: string) => {
    if (searchQuery.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchAddressesFR(searchQuery);

      setSuggestions(results);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresse:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    
    onChange?.(newValue);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      searchAddresses(newValue);
    }, 300);
    
    setDebounceTimer(timer);
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    // Extraire le code région depuis le context
    const regionCode = extractRegionCode(suggestion.properties.context);

    const addressData: AddressData = {
      address: suggestion.properties.name,
      label: suggestion.properties.name,
      postcode: suggestion.properties.postcode,
      postalCode: suggestion.properties.postcode,
      city: suggestion.properties.city,
      regionCode: regionCode
    };

    setQuery(suggestion.properties.name);
    setShowSuggestions(false);
    onAddressSelect(addressData);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        onBlur?.();
      }
    }, 150);
  };

  const id = useId();
  const inputClasses = `input-field pr-10 ${error ? 'input-error' : ''} ${className}`;

  return (
    <div className="field-group">
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="form-label-required">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={inputClasses}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-autocomplete="list"
          aria-expanded={showSuggestions && suggestions.length > 0}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
          ) : (
            <MapPin className="h-4 w-4 text-neutral-400" />
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 transition-colors flex items-start gap-2 ${
                  selectedIndex === index ? 'bg-neutral-50' : ''
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <MapPin className="h-4 w-4 text-neutral-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate text-neutral-900">
                    {suggestion.properties.name}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    {suggestion.properties.postcode} {suggestion.properties.city}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {error && (
        <p id={`${id}-error`} className="error-message">{error}</p>
      )}
    </div>
  );
}